import { PermissionContext } from '@ardoq/access-control';
import { userOperations } from '@ardoq/core';
import { isTruthy } from '@ardoq/common-helpers';
import {
  action$,
  extractPayload,
  ofType,
  persistentReducedStream,
  spreadMap,
  streamReducer,
} from '@ardoq/rxbeach';
import { combineLatest, Observable, startWith } from 'rxjs';
import { dishonestCurrentUser$ as currentUser$ } from 'streams/currentUser/currentUser$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { slides$ } from 'streams/slides/slides$';
import { slideOperations } from 'streams/slides/slideOperations';
import {
  getMinimumSlideKillDateForPresentation,
  getOverallPresentationViewInfo,
} from './DepreciatedSlideNotificationUtil';
import { getDeprecatedSlidesInfo } from './util';
import {
  type MetaInfo,
  MetaInfoViewStatus,
} from 'streams/views/mainContent/types';
import { DeprecatedSlidesState, SlidesMigrationInfo } from './types';
import { setSlidesMigrationInfo } from 'presentation/actions';
import { toPresentationAsset } from 'presentation/toPresentationAsset';
import { APICurrentUser, APISlideAttributes, ArdoqId } from '@ardoq/api-types';
import { deprecatedViews$ } from 'views/deprecatedViews$';

const DEFAULT_STATE: DeprecatedSlidesState = {
  activePresentation: null,
  overallPresentationViewInfo: null,
  deprecatedSlides: [],
  minimumKillDate: null,
  permissionContext: null,
  creatorCanMigrateNothing: true,
  creatorCanMigrateSome: false,
  discontinuedSlidesPresent: false,
  shouldShowNotification: false,
};

type NotificationSourceDataStreamShape = SlidesMigrationInfo & {
  currentUser: APICurrentUser;
  permissionContext: PermissionContext;
  slidesById: Record<ArdoqId, APISlideAttributes>;
  deprecatedViews: MetaInfo[];
};

const resetState = (
  _: DeprecatedSlidesState,
  sourceData: NotificationSourceDataStreamShape
) => {
  const { slidesById, deprecatedViews, ...deprecationInfo } = sourceData;
  const { currentUser, activePresentation, slidesInfo, permissionContext } =
    deprecationInfo;

  if (!activePresentation) {
    return {
      ...DEFAULT_STATE,
      ...deprecationInfo,
    };
  }

  const currentUserIsOrgAdmin = userOperations.hasAdminRole(currentUser);

  const slideInfos = (activePresentation.slides ?? [])
    .map(slideId => slidesById[slideId])
    .filter(isTruthy)
    .map(slide => slideOperations.getSlideInfo(slide, permissionContext));
  const deprecatedSlides = getDeprecatedSlidesInfo(slideInfos, deprecatedViews);
  const overallPresentationViewInfo = getOverallPresentationViewInfo(
    toPresentationAsset(activePresentation),
    deprecatedSlides,
    permissionContext
  );

  const shouldShowNotification = Boolean(
    activePresentation?._id &&
    overallPresentationViewInfo?.overallDeprecationPhase &&
    overallPresentationViewInfo?.overallPermissionsLevel &&
    deprecatedSlides?.length
  );
  if (!shouldShowNotification) {
    return {
      ...DEFAULT_STATE,
      ...deprecationInfo,
      overallPresentationViewInfo,
      shouldShowNotification,
    };
  }

  const discontinuedSlidesPresent = !!deprecatedSlides.filter(
    slideInfo => slideInfo.viewStatus === MetaInfoViewStatus.DISCONTINUED
  ).length;
  const creatorCanMigrateNothing =
    deprecatedSlides.every(
      ({ slideId }) => !slidesInfo?.[slideId]?.creatorCanMigrate
    ) && !currentUserIsOrgAdmin;
  const creatorCanMigrateSome =
    !creatorCanMigrateNothing &&
    deprecatedSlides.some(
      ({ slideId }) => !slidesInfo?.[slideId]?.creatorCanMigrate
    );
  const minimumKillDate =
    getMinimumSlideKillDateForPresentation(deprecatedSlides);

  return {
    ...deprecationInfo,
    overallPresentationViewInfo,
    deprecatedSlides,
    minimumKillDate,
    creatorCanMigrateNothing,
    creatorCanMigrateSome,
    discontinuedSlidesPresent,
    shouldShowNotification,
  };
};

const defaultSlideMigrationInfo: SlidesMigrationInfo = {
  activePresentation: null,
  slidesInfo: undefined,
  activeSlide: undefined,
};

const reset$ = action$.pipe(
  ofType(setSlidesMigrationInfo),
  extractPayload(),
  startWith(defaultSlideMigrationInfo)
);

const deprecatedSlidesSourceData$: Observable<NotificationSourceDataStreamShape> =
  combineLatest([
    reset$,
    currentUser$,
    currentUserPermissionContext$,
    slides$,
    deprecatedViews$,
  ]).pipe(
    spreadMap(
      (
        slidesMigrationInfo,
        currentUser,
        permissionContext,
        { byId },
        deprecatedViews
      ) => ({
        currentUser,
        permissionContext,
        slidesById: byId,
        deprecatedViews,
        ...slidesMigrationInfo,
      })
    )
  );

export const deprecatedSlides$ = persistentReducedStream<DeprecatedSlidesState>(
  'deprecatedSlideNotification',
  DEFAULT_STATE,
  [
    streamReducer<DeprecatedSlidesState, NotificationSourceDataStreamShape>(
      deprecatedSlidesSourceData$,
      resetState
    ),
  ]
);
